/**
 * STATUS & SUPPORT WIDGET
 * 
 * A configurable health monitoring and support widget for React apps.
 * Designed for multi-tenant deployments with per-tenant configuration.
 * 
 * USAGE:
 * import { StatusWidget } from './components/SupportWidget';
 * <StatusWidget config={widgetConfig} />
 */

import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import './StatusWidget.css';

// ============================================================================
// DEFAULT CONFIGURATION
// ============================================================================

const defaultConfig = {
  companyName: "Your Company",
  clientName: "", // For webhook payload
  logoUrl: "",
  supportEmail: "support@company.com",
  webhookUrl: "",
  healthCheckEndpoint: "/api/health",
  healthCheckInterval: 30000,
  brandColor: "#0EA5E9",
  position: "bottom-right",
  enabled: true,
  // Docs tab config (Crown Mark Docs Widget)
  docsEnabled: false,
  docsSupabaseUrl: "",
  docsSupabaseAnonKey: "",
  docsIframeUrl: "", // Alternative: direct iframe URL
};

// ============================================================================
// MAIN WIDGET COMPONENT
// ============================================================================

export function StatusWidget({ config = {} }) {
  const widgetConfig = { ...defaultConfig, ...config };

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("health");
  const [health, setHealth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const panelRef = useRef(null);
  const previousHealthRef = useRef(null);
  const alertSentRef = useRef(new Set());

  const [supportMessage, setSupportMessage] = useState("");
  const [isSendingSupport, setIsSendingSupport] = useState(false);
  const [supportSent, setSupportSent] = useState(false);
  const [isDocsExpanded, setIsDocsExpanded] = useState(false);
  const [expandedEmployees, setExpandedEmployees] = useState({});

  // ============================================================================
  // AUTOMATIC ALERT SYSTEM
  // ============================================================================

  const sendAutomaticAlert = useCallback(
    async (downServices, currentHealth) => {
      if (!widgetConfig.webhookUrl) return;

      try {
        // Use server-side endpoint with deduplication
        const alertEndpoint = window.location.hostname === 'localhost'
          ? 'http://localhost:8888/.netlify/functions/send-alert'
          : '/.netlify/functions/send-alert';

        const response = await fetch(alertEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            downServices,
            currentHealth,
            config: {
              webhookUrl: widgetConfig.webhookUrl,
              companyName: widgetConfig.companyName,
              clientName: widgetConfig.clientName || widgetConfig.companyName,
            }
          }),
        });

        const result = await response.json();
        console.log("StatusWidget: Alert result:", result);
      } catch (error) {
        console.error("StatusWidget: Failed to send automatic alert:", error);
      }
    },
    [widgetConfig.webhookUrl, widgetConfig.companyName, widgetConfig.clientName]
  );

  // ============================================================================
  // HEALTH CHECK SYSTEM
  // ============================================================================

  const checkHealth = useCallback(
    async (isInitial = false) => {
      if (!widgetConfig.healthCheckEndpoint) return;
      if (!isInitial) setIsLoading(true);

      try {
        const response = await fetch(widgetConfig.healthCheckEndpoint);
        const data = await response.json();

        if (previousHealthRef.current && !isInitial && widgetConfig.webhookUrl) {
          const newlyDownServices = [];
          for (const service of data.services) {
            const prev = previousHealthRef.current.services.find((s) => s.name === service.name);
            const wasHealthy = !prev || prev.status === "healthy" || prev.status === "unconfigured";
            const isNowDown = service.status === "down" || service.status === "degraded";
            if (wasHealthy && isNowDown && !alertSentRef.current.has(service.name)) {
              newlyDownServices.push(service);
              alertSentRef.current.add(service.name);
            }
            if (service.status === "healthy" && alertSentRef.current.has(service.name)) {
              alertSentRef.current.delete(service.name);
            }
          }
          if (newlyDownServices.length > 0) sendAutomaticAlert(newlyDownServices, data);
        }

        previousHealthRef.current = data;
        setHealth(data);
        setLastChecked(new Date());
      } catch (error) {
        console.error("StatusWidget: Health check failed:", error);
        setHealth({
          status: "down",
          healthy: 0,
          total: 1,
          services: [{ name: "API", status: "down", message: "Unable to reach health endpoint" }],
        });
      } finally {
        setIsLoading(false);
      }
    },
    [widgetConfig.healthCheckEndpoint, widgetConfig.webhookUrl, sendAutomaticAlert]
  );

  useEffect(() => {
    checkHealth(true);
    const intervalId = setInterval(() => checkHealth(false), widgetConfig.healthCheckInterval);
    return () => clearInterval(intervalId);
  }, [checkHealth, widgetConfig.healthCheckInterval]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // ============================================================================
  // SUPPORT MESSAGE HANDLER
  // ============================================================================

  const handleSendSupport = async () => {
    if (!supportMessage.trim()) return;
    if (!widgetConfig.webhookUrl) {
      alert("Support messaging is not configured. Please contact " + widgetConfig.supportEmail);
      return;
    }

    setIsSendingSupport(true);
    console.log("StatusWidget: Sending support message...");
    
    try {
      const payload = {
        message: supportMessage,
        timestamp: new Date().toISOString(),
        alertType: "manual",
        companyName: widgetConfig.companyName,
        client_name: widgetConfig.clientName || widgetConfig.companyName,
        apiHealth: health || { status: "unknown", services: [] },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        url: typeof window !== "undefined" ? window.location.href : "unknown",
      };

      console.log("StatusWidget: Payload:", payload);
      console.log("StatusWidget: Webhook URL:", widgetConfig.webhookUrl);

      await fetch(widgetConfig.webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "no-cors",
        body: JSON.stringify(payload),
      });

      console.log("StatusWidget: Message sent successfully (no-cors mode - response opaque)");
      
      setSupportSent(true);
      setSupportMessage("");
      setTimeout(() => setSupportSent(false), 3000);
    } catch (error) {
      console.error("StatusWidget: Failed to send support message:", error);
      alert("Failed to send message. Please try again or email " + widgetConfig.supportEmail);
    } finally {
      setIsSendingSupport(false);
    }
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const getStatusColor = (status) => {
    const colors = {
      healthy: "#10b981",
      degraded: "#f59e0b",
      down: "#ef4444",
      unconfigured: "#94a3b8",
    };
    return colors[status] || colors.unconfigured;
  };

  const getStatusIcon = (status) => {
    const icons = { healthy: "✓", degraded: "⚠", down: "✕", unconfigured: "○" };
    return icons[status] || "?";
  };

  const getOverallStatusColor = () => {
    if (isLoading && !health) return "#94a3b8";
    if (!health) return "#94a3b8";
    if (health.services.some((s) => s.status === "down")) return "#ef4444";
    if (health.services.some((s) => s.status === "degraded")) return "#f59e0b";
    if (health.status === "healthy") return "#10b981";
    return "#94a3b8";
  };

  const shouldPulse = () => {
    if (isLoading && !health) return true;
    if (!health) return false;
    return health.status !== "healthy";
  };

  const toggleEmployee = (index) => {
    setExpandedEmployees(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Don't render if disabled (after all hooks)
  if (!widgetConfig.enabled) return null;

  // ============================================================================
  // INLINE STYLES (to avoid Tailwind dependency issues)
  // ============================================================================

  const styles = {
    container: {
      position: "fixed",
      bottom: "1rem",
      right: "1rem",
      zIndex: 9999,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    fabOuter: {
      position: "relative",
      padding: "2px",
      borderRadius: "1rem",
      background: "linear-gradient(135deg, #38bdf8, #14b8a6, #10b981)",
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 0 15px rgba(20, 184, 166, 0.3)",
      cursor: "pointer",
      transition: "transform 0.2s, box-shadow 0.2s",
    },
    fabInner: {
      position: "relative",
      width: "52px",
      height: "52px",
      borderRadius: "14px",
      background: "rgba(255, 255, 255, 0.97)",
      backdropFilter: "blur(12px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "none",
      cursor: "pointer",
    },
    indicator: {
      position: "absolute",
      top: "4px",
      right: "4px",
      width: "12px",
      height: "12px",
      borderRadius: "50%",
      backgroundColor: getOverallStatusColor(),
      border: "2px solid white",
      boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
      animation: shouldPulse() ? "pulse 2s infinite" : "none",
    },
    panel: {
      position: "absolute",
      bottom: "70px",
      right: "0",
      width: "360px",
      maxHeight: "480px",
      borderRadius: "16px",
      background: "rgba(255, 255, 255, 0.98)",
      backdropFilter: "blur(20px)",
      border: "1px solid #e2e8f0",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0,0,0,0.05)",
      overflow: "hidden",
      animation: "slideUp 0.25s ease-out",
    },
    tabs: {
      display: "flex",
      borderBottom: "1px solid #f1f5f9",
    },
    tab: {
      flex: 1,
      padding: "14px 16px",
      fontSize: "13px",
      fontWeight: 500,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      transition: "all 0.2s",
    },
    tabActive: {
      color: "#0284c7",
      borderBottom: "2px solid #0ea5e9",
      background: "rgba(240, 249, 255, 0.5)",
    },
    tabInactive: {
      color: "#64748b",
      borderBottom: "2px solid transparent",
    },
    header: {
      padding: "14px 16px",
      borderBottom: "1px solid #f1f5f9",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    statusDot: {
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      backgroundColor: getOverallStatusColor(),
    },
    companyName: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#1e293b",
      margin: 0,
    },
    refreshBtn: {
      padding: "6px",
      borderRadius: "8px",
      border: "none",
      background: "transparent",
      cursor: "pointer",
      color: "#64748b",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s",
    },
    summary: {
      padding: "12px 16px",
      background: "#f8fafc",
      borderBottom: "1px solid #f1f5f9",
    },
    summaryRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    summaryLabel: {
      fontSize: "12px",
      color: "#64748b",
    },
    statusBadge: {
      fontSize: "11px",
      fontWeight: 500,
      padding: "3px 10px",
      borderRadius: "12px",
    },
    serviceList: {
      maxHeight: "280px",
      overflowY: "auto",
    },
    serviceItem: {
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      borderBottom: "1px solid #f1f5f9",
      transition: "background 0.2s",
    },
    serviceLeft: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    serviceIcon: {
      width: "28px",
      height: "28px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontSize: "12px",
      fontWeight: 700,
    },
    serviceName: {
      fontSize: "13px",
      fontWeight: 500,
      color: "#334155",
      margin: 0,
    },
    serviceStatus: {
      fontSize: "11px",
      color: "#94a3b8",
      margin: "2px 0 0 0",
    },
    latency: {
      fontSize: "11px",
      color: "#94a3b8",
    },
    footer: {
      padding: "10px 16px",
      background: "#f8fafc",
      borderTop: "1px solid #f1f5f9",
    },
    footerText: {
      fontSize: "11px",
      color: "#94a3b8",
      textAlign: "center",
      margin: 0,
    },
    supportContent: {
      padding: "20px 16px",
      minHeight: "420px",
      display: "flex",
      flexDirection: "column",
    },
    supportTitle: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#1e293b",
      margin: "0 0 4px 0",
    },
    supportDesc: {
      fontSize: "12px",
      color: "#64748b",
      margin: "0 0 16px 0",
    },
    label: {
      display: "block",
      fontSize: "12px",
      fontWeight: 500,
      color: "#475569",
      marginBottom: "6px",
    },
    textarea: {
      width: "100%",
      padding: "10px 12px",
      fontSize: "13px",
      border: "1px solid #e2e8f0",
      borderRadius: "10px",
      resize: "none",
      outline: "none",
      fontFamily: "inherit",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxSizing: "border-box",
    },
    sendBtn: {
      width: "100%",
      padding: "12px",
      marginTop: "12px",
      background: "linear-gradient(135deg, #0ea5e9, #14b8a6)",
      color: "white",
      fontSize: "13px",
      fontWeight: 500,
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      transition: "opacity 0.2s, transform 0.2s",
    },
    emailLink: {
      marginTop: "16px",
      paddingTop: "16px",
      borderTop: "1px solid #f1f5f9",
      fontSize: "12px",
      color: "#94a3b8",
      textAlign: "center",
    },
    successIcon: {
      width: "48px",
      height: "48px",
      borderRadius: "50%",
      background: "#d1fae5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 12px",
    },
    successText: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#1e293b",
      textAlign: "center",
      margin: "0 0 4px 0",
    },
    successSubtext: {
      fontSize: "12px",
      color: "#64748b",
      textAlign: "center",
      margin: 0,
    },
    loader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "32px",
    },
    spinner: {
      width: "24px",
      height: "24px",
      border: "2px solid #e2e8f0",
      borderTopColor: "#0ea5e9",
      borderRadius: "50%",
      animation: "spin 0.8s linear infinite",
    },
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div style={styles.container} ref={panelRef}>
      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; maxHeight: 0; }
          to { opacity: 1; maxHeight: 500px; }
        }
      `}</style>

      {/* Floating Action Button */}
      <div
        style={styles.fabOuter}
        onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
        onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={styles.fabInner}
          aria-label={isOpen ? "Close status widget" : "Open status widget"}
        >
          {/* Status indicator dot - only show when closed */}
          {!isOpen && <span style={styles.indicator} />}
          {/* Logo/Arrow container with transform animation */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 0.3s ease-in-out, opacity 0.2s ease-in-out",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}>
            {isOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.5">
                <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : widgetConfig.logoUrl ? (
              <img 
                src={widgetConfig.logoUrl} 
                alt="Status" 
                style={{ 
                  width: "42px", 
                  height: "42px", 
                  objectFit: "contain",
                  borderRadius: "6px",
                }} 
              />
            ) : (
              /* Default: Cloud Intelligence Icon (Shoofly-style) */
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                {/* Cloud shape */}
                <path 
                  d="M6.5 19C4.01 19 2 16.99 2 14.5C2 12.5 3.4 10.8 5.27 10.26C5.1 9.87 5 9.44 5 9C5 7.34 6.34 6 8 6C8.27 6 8.53 6.03 8.78 6.09C9.63 4.22 11.47 3 13.5 3C16.54 3 19 5.46 19 8.5C19 8.83 18.97 9.16 18.92 9.47C20.66 10.02 22 11.64 22 13.5C22 15.99 19.99 18 17.5 18" 
                  stroke="#0ea5e9" 
                  strokeWidth="1.8" 
                  strokeLinecap="round"
                  fill="rgba(14, 165, 233, 0.1)"
                />
                {/* Brain/Intelligence nodes */}
                <circle cx="10" cy="13" r="1.5" fill="#0ea5e9"/>
                <circle cx="14" cy="11" r="1.5" fill="#14b8a6"/>
                <circle cx="12" cy="16" r="1.5" fill="#10b981"/>
                {/* Connection lines */}
                <path d="M10 13L14 11M14 11L12 16M12 16L10 13" stroke="#64748b" strokeWidth="1" strokeLinecap="round" opacity="0.6"/>
              </svg>
            )}
          </div>
        </button>
      </div>

      {/* Panel */}
      {isOpen && (
        <div style={styles.panel}>
          {/* Tabs */}
          <div style={styles.tabs}>
            <button
              onClick={() => setActiveTab("health")}
              style={{ ...styles.tab, ...(activeTab === "health" ? styles.tabActive : styles.tabInactive) }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Tools
            </button>
            <button
              onClick={() => setActiveTab("ai")}
              style={{ ...styles.tab, ...(activeTab === "ai" ? styles.tabActive : styles.tabInactive) }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              AI
            </button>
            <button
              onClick={() => setActiveTab("support")}
              style={{ ...styles.tab, ...(activeTab === "support" ? styles.tabActive : styles.tabInactive) }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Support
            </button>
            {widgetConfig.docsEnabled && (
              <button
                onClick={() => setActiveTab("docs")}
                style={{ ...styles.tab, ...(activeTab === "docs" ? styles.tabActive : styles.tabInactive) }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Docs
              </button>
            )}
          </div>

          {/* AI Tab - Collapsible AI Employees */}
          {activeTab === "ai" && (
            <div style={{ minHeight: "420px" }}>
              {/* Compact Header */}
              <div style={{
                padding: "10px 14px",
                background: "linear-gradient(135deg, #0ea5e9 0%, #14b8a6 100%)",
                borderBottom: "1px solid rgba(255,255,255,0.1)"
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "6px",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: "white", letterSpacing: "-0.01em" }}>
                        AI Workforce
                      </h3>
                      <p style={{ margin: "1px 0 0 0", fontSize: "9px", color: "rgba(255,255,255,0.75)" }}>
                        Intelligent Automation
                      </p>
                    </div>
                  </div>
                  
                  {health?.aiEmployees && (
                    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                      <div style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        background: "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(10px)"
                      }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "white", lineHeight: 1 }}>
                          {health.aiEmployees.filter(e => e.status === 'active').length}
                        </div>
                        <div style={{ fontSize: "8px", color: "rgba(255,255,255,0.75)", marginTop: "2px" }}>
                          Active
                        </div>
                      </div>
                      <div style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        background: "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(10px)"
                      }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "white", lineHeight: 1 }}>
                          {health.aiEmployees.filter(e => e.overallHealth === 'healthy').length}/{health.aiEmployees.length}
                        </div>
                        <div style={{ fontSize: "8px", color: "rgba(255,255,255,0.75)", marginTop: "2px" }}>
                          OK
                        </div>
                      </div>
                      <button
                        onClick={() => checkHealth(false)}
                        disabled={isLoading}
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "6px",
                          background: "rgba(255,255,255,0.2)",
                          border: "none",
                          cursor: isLoading ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.2s",
                          opacity: isLoading ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => !isLoading && (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
                        onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
                      >
                        <svg
                          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
                          style={{ animation: isLoading ? "spin 0.8s linear infinite" : "none" }}
                        >
                          <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Instructional Text */}
              <div style={{
                padding: "8px 14px",
                background: "#f0f9ff",
                borderBottom: "1px solid #bae6fd",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                </svg>
                <span style={{ fontSize: "10px", color: "#0369a1", fontWeight: 500 }}>
                  Click any employee to view platform health & dependencies
                </span>
              </div>

              {/* AI Employees List */}
              <div style={{ 
                maxHeight: "320px", 
                overflowY: "auto", 
                padding: "8px 8px 16px 8px",
                scrollbarWidth: "thin"
              }}>
                {isLoading && !health ? (
                  <div style={{ padding: "40px 0", display: "flex", justifyContent: "center" }}>
                    <div style={styles.spinner} />
                  </div>
                ) : health?.aiEmployees ? (
                  <>
                  {health.aiEmployees.map((employee, idx) => {
                    const isExpanded = expandedEmployees[idx];
                    const healthColor = 
                      employee.overallHealth === 'healthy' ? '#10b981' :
                      employee.overallHealth === 'degraded' ? '#f59e0b' :
                      employee.overallHealth === 'down' ? '#ef4444' : '#94a3b8';
                    
                    return (
                      <div 
                        key={idx} 
                        style={{
                          marginBottom: "8px",
                          borderRadius: "12px",
                          overflow: "hidden",
                          background: "white",
                          border: `2px solid ${isExpanded ? healthColor : '#e2e8f0'}`,
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          boxShadow: isExpanded ? `0 4px 12px ${healthColor}33` : "0 1px 3px rgba(0,0,0,0.1)"
                        }}
                      >
                        {/* Employee Header (Clickable) */}
                        <div
                          onClick={() => toggleEmployee(idx)}
                          style={{
                            padding: "14px 16px",
                            cursor: "pointer",
                            background: isExpanded ? `linear-gradient(135deg, ${healthColor}15, ${healthColor}05)` : "white",
                            transition: "all 0.2s",
                            userSelect: "none"
                          }}
                          onMouseEnter={(e) => {
                            if (!isExpanded) e.currentTarget.style.background = "#f8fafc";
                          }}
                          onMouseLeave={(e) => {
                            if (!isExpanded) e.currentTarget.style.background = "white";
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            {/* Expand/Collapse Icon */}
                            <div style={{
                              width: "24px",
                              height: "24px",
                              borderRadius: "6px",
                              background: `${healthColor}15`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "transform 0.3s"
                            }}>
                              <svg 
                                width="14" 
                                height="14" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke={healthColor} 
                                strokeWidth="3"
                                style={{
                                  transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                  transition: "transform 0.3s"
                                }}
                              >
                                <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>

                            {/* Status Indicator */}
                            <div style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: healthColor,
                              boxShadow: `0 0 0 3px ${healthColor}20`,
                              animation: employee.status === 'active' ? "pulse 2s infinite" : "none"
                            }} />

                            {/* Employee Info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                                <span style={{
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#1e293b",
                                  letterSpacing: "-0.01em"
                                }}>
                                  {employee.name}
                                </span>
                              </div>
                              <div style={{ fontSize: "11px", color: "#64748b", lineHeight: 1.4 }}>
                                {employee.description}
                              </div>
                            </div>

                            {/* Status Badge */}
                            <span style={{
                              fontSize: "10px",
                              fontWeight: 600,
                              padding: "4px 8px",
                              borderRadius: "6px",
                              background: `${healthColor}15`,
                              color: healthColor,
                              textTransform: "uppercase",
                              letterSpacing: "0.5px"
                            }}>
                              {employee.status}
                            </span>
                          </div>
                        </div>

                        {/* Expandable Dependencies Section */}
                        {isExpanded && (
                          <div style={{
                            padding: "0 16px 16px 16px",
                            background: `${healthColor}05`,
                            animation: "slideDown 0.3s ease-out"
                          }}>
                            {/* Metrics */}
                            <div style={{
                              display: "flex",
                              gap: "8px",
                              padding: "12px",
                              marginBottom: "12px",
                              background: "white",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0"
                            }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: "10px", color: "#64748b", marginBottom: "4px" }}>⏱ Speed</div>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b" }}>
                                  {employee.metrics.processing}
                                </div>
                              </div>
                              <div style={{ width: "1px", background: "#e2e8f0" }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: "10px", color: "#64748b", marginBottom: "4px" }}>📅 Schedule</div>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b" }}>
                                  {employee.schedule}
                                </div>
                              </div>
                            </div>

                            {/* Dependencies/Tools */}
                            <div style={{ fontSize: "10px", fontWeight: 600, color: "#64748b", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              Dependencies ({employee.dependencies.length})
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                              {employee.dependencies.map((dep, depIdx) => (
                                <div 
                                  key={depIdx}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "10px 12px",
                                    background: "white",
                                    borderRadius: "8px",
                                    border: `1px solid ${dep.status === 'healthy' ? '#d1fae5' : dep.status === 'degraded' ? '#fef3c7' : '#fee2e2'}`,
                                    transition: "all 0.2s"
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.transform = "translateX(4px)"}
                                  onMouseLeave={(e) => e.currentTarget.style.transform = "translateX(0)"}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <span style={{
                                      width: "6px",
                                      height: "6px",
                                      borderRadius: "50%",
                                      backgroundColor: getStatusColor(dep.status),
                                      boxShadow: `0 0 0 2px ${getStatusColor(dep.status)}30`
                                    }} />
                                    <span style={{ fontSize: "12px", fontWeight: 500, color: "#334155" }}>
                                      {dep.name}
                                    </span>
                                  </div>
                                  <span style={{
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    color: dep.status === 'healthy' ? '#059669' :
                                           dep.status === 'degraded' ? '#d97706' :
                                           dep.status === 'down' ? '#dc2626' : '#64748b'
                                  }}>
                                    {getStatusIcon(dep.status)} {dep.status}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Bottom spacer to prevent cut-off */}
                  <div style={{ height: "8px" }} />
                  </>
                ) : (
                  <div style={{ padding: "40px 16px", textAlign: "center" }}>
                    <div style={{ fontSize: "32px", marginBottom: "8px" }}>🤖</div>
                    <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0 }}>No AI employee data available</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              {lastChecked && (
                <div style={{
                  padding: "12px 16px",
                  background: "#f8fafc",
                  borderTop: "1px solid #e2e8f0"
                }}>
                  <p style={{ fontSize: "10px", color: "#94a3b8", textAlign: "center", margin: 0 }}>
                    Last checked: {lastChecked.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    {" · Auto-refresh every " + (widgetConfig.healthCheckInterval / 1000) + "s"}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Tools/Health Tab - Platform Status */}
          {activeTab === "health" && (
            <>
              {/* Header */}
              <div style={styles.header}>
                <div style={styles.headerLeft}>
                  <div style={styles.statusDot} />
                  <h3 style={styles.companyName}>{widgetConfig.companyName}</h3>
                </div>
                <button
                  onClick={() => checkHealth(false)}
                  disabled={isLoading}
                  style={{ ...styles.refreshBtn, opacity: isLoading ? 0.5 : 1, cursor: isLoading ? "not-allowed" : "pointer" }}
                  onMouseEnter={(e) => !isLoading && (e.currentTarget.style.background = "#f1f5f9")}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  title="Refresh"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ animation: isLoading ? "spin 0.8s linear infinite" : "none" }}
                  >
                    <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              {/* Summary */}
              {health && (
                <div style={styles.summary}>
                  <div style={styles.summaryRow}>
                    <span style={styles.summaryLabel}>Overall Status</span>
                    <span
                      style={{
                        ...styles.statusBadge,
                        background: health.status === "healthy" ? "#d1fae5" : health.status === "degraded" ? "#fef3c7" : "#fee2e2",
                        color: health.status === "healthy" ? "#065f46" : health.status === "degraded" ? "#92400e" : "#991b1b",
                      }}
                    >
                      {health.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              )}

              {/* Services List */}
              {isLoading && !health ? (
                <div style={styles.loader}>
                  <div style={styles.spinner} />
                </div>
              ) : (
                <div style={styles.serviceList}>
                  {health?.services.map((service, idx) => (
                    <div key={idx} style={styles.serviceItem}>
                      <div style={styles.serviceLeft}>
                        <div style={{ ...styles.serviceIcon, backgroundColor: getStatusColor(service.status) }}>
                          {getStatusIcon(service.status)}
                        </div>
                        <div>
                          <p style={styles.serviceName}>{service.name}</p>
                          <p style={styles.serviceStatus}>{service.message}</p>
                        </div>
                      </div>
                      {service.latency > 0 && <span style={styles.latency}>{service.latency}ms</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Footer */}
              {lastChecked && (
                <div style={styles.footer}>
                  <p style={styles.footerText}>
                    Last checked: {lastChecked.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Support Tab */}
          {activeTab === "support" && (
            <div style={styles.supportContent}>
              {supportSent ? (
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                  <div style={styles.successIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h3 style={styles.successText}>Message Sent!</h3>
                  <p style={styles.successSubtext}>We'll get back to you soon.</p>
                </div>
              ) : (
                <>
                  <h3 style={styles.supportTitle}>Need Help?</h3>
                  <p style={styles.supportDesc}>Send us a message and we'll respond as soon as possible.</p>
                  
                  <label style={styles.label}>Message</label>
                  <textarea
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    placeholder="Describe your issue or question..."
                    rows={4}
                    style={styles.textarea}
                    onFocus={(e) => { e.target.style.borderColor = "#0ea5e9"; e.target.style.boxShadow = "0 0 0 3px rgba(14, 165, 233, 0.1)"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                  />

                  <button
                    onClick={handleSendSupport}
                    disabled={!supportMessage.trim() || isSendingSupport}
                    style={{ ...styles.sendBtn, opacity: (!supportMessage.trim() || isSendingSupport) ? 0.5 : 1, cursor: (!supportMessage.trim() || isSendingSupport) ? "not-allowed" : "pointer" }}
                  >
                    {isSendingSupport ? (
                      <>
                        <div style={{ ...styles.spinner, width: "16px", height: "16px", borderColor: "rgba(255,255,255,0.3)", borderTopColor: "white" }} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Send Message
                      </>
                    )}
                  </button>

                  <div style={styles.emailLink}>
                    Or email us at{" "}
                    <a href={`mailto:${widgetConfig.supportEmail}`} style={{ color: "#0ea5e9", textDecoration: "none" }}>
                      {widgetConfig.supportEmail}
                    </a>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Docs Tab */}
          {activeTab === "docs" && widgetConfig.docsEnabled && (
            <div style={{ height: "420px", overflow: "hidden", position: "relative" }}>
              {widgetConfig.docsIframeUrl ? (
                <>
                  <iframe
                    src={widgetConfig.docsIframeUrl}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    title="Documentation"
                  />
                  {/* Expand Button */}
                  <button
                    onClick={() => setIsDocsExpanded(true)}
                    style={{
                      position: "absolute",
                      top: "12px",
                      right: "12px",
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      background: "rgba(255, 255, 255, 0.95)",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.transform = "scale(1.05)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255, 255, 255, 0.95)"; e.currentTarget.style.transform = "scale(1)"; }}
                    title="Expand documentation"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2">
                      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </>
              ) : (
                <div style={styles.supportContent}>
                  <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" style={{ marginBottom: "16px" }}>
                      <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <h3 style={{ ...styles.supportTitle, marginBottom: "8px" }}>Documentation</h3>
                    <p style={{ ...styles.supportDesc, marginBottom: "0" }}>
                      Configure <code style={{ background: "#f1f5f9", padding: "2px 6px", borderRadius: "4px", fontSize: "11px" }}>docsIframeUrl</code> to embed your docs here.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Expanded Docs Modal */}
      {isDocsExpanded && widgetConfig.docsIframeUrl && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "fadeIn 0.2s ease-out",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsDocsExpanded(false);
          }}
        >
          <div
            style={{
              width: "90vw",
              maxWidth: "1200px",
              height: "85vh",
              background: "white",
              borderRadius: "16px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.4)",
              overflow: "hidden",
              animation: "scaleIn 0.25s ease-out",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#f8fafc",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2">
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "#1e293b" }}>
                  Documentation
                </h2>
              </div>
              <button
                onClick={() => setIsDocsExpanded(false)}
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  background: "white",
                  border: "1px solid #e2e8f0",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.borderColor = "#fca5a5"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "white"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
                title="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            {/* Modal Content */}
            <iframe
              src={widgetConfig.docsIframeUrl}
              style={{
                flex: 1,
                width: "100%",
                border: "none",
              }}
              title="Documentation"
            />
          </div>
        </div>
      )}

      {/* Additional CSS for modal animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

StatusWidget.propTypes = {
  config: PropTypes.shape({
    companyName: PropTypes.string,
    logoUrl: PropTypes.string,
    supportEmail: PropTypes.string,
    webhookUrl: PropTypes.string,
    healthCheckEndpoint: PropTypes.string,
    healthCheckInterval: PropTypes.number,
    brandColor: PropTypes.string,
    position: PropTypes.oneOf(["bottom-right", "bottom-left", "top-right", "top-left"]),
    enabled: PropTypes.bool,
  }),
};

export default StatusWidget;
